export interface SiteValidationIssues {
    hardErrors: string[];
    softWarnings: string[];
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const hasValidOperatingHours = (oh: any): boolean => {
    if (!oh || typeof oh !== 'object') return false;
    const hasAnyDay = DAYS.some((d) => oh[d]?.open && oh[d]?.close);
    const hasHolidays = !!(oh.publicHolidays?.open || oh.publicHolidays?.closed);
    return hasAnyDay || hasHolidays;
};

const hasAmenities = (values: any): boolean => {
    const a = values?.amenities;
    if (!a) return false;
    if (Array.isArray(a)) return a.length > 0;
    if (typeof a === 'object') return Object.keys(a).length > 0;
    return false;
};

const isPaidParkingType = (parkingType: string | undefined | null): boolean => {
    return parkingType === 'lot' || parkingType === 'dedicated';
};

const hasParkingRates = (values: any): boolean => {
    const pi = values?.parkingInfo;
    if (!pi || typeof pi !== 'object') return false;
    return pi.weekdayRate != null || pi.weekendRate != null || pi.publicHolidayRate != null;
};

const needsOperatingHours = (values: any): boolean => {
    const access = values?.operatingAccess || '24_7';
    if (access === '24_7' || access === 'closed') return false;
    return !hasValidOperatingHours(values?.operatingHours);
};

type Translate = (key: string, options?: any) => string;

export const computeSiteValidationIssues = (values: any, translate: Translate): SiteValidationIssues => {
    const hardErrors: string[] = [];
    const softWarnings: string[] = [];

    if (needsOperatingHours(values)) {
        hardErrors.push(
            translate('resources.sites.validation.operatingHoursRequired', {
                _: 'Operating Hours — set at least one day\'s open/close times (required when access is not 24/7)',
            })
        );
    }

    if (!values?.primaryImage) {
        softWarnings.push(
            translate('resources.sites.validation.primaryImageWarning', {
                _: 'No hero image — site detail page will show a placeholder image',
            })
        );
    }

    if (!hasAmenities(values)) {
        softWarnings.push(
            translate('resources.sites.validation.amenitiesWarning', {
                _: 'No amenities selected — site will not appear in amenity filters on the map',
            })
        );
    }

    if (isPaidParkingType(values?.parkingType) && !hasParkingRates(values)) {
        softWarnings.push(
            translate('resources.sites.validation.parkingRatesWarning', {
                _: 'Paid parking type selected but no parking rates set (weekday/weekend/holiday rate is empty)',
            })
        );
    }

    return { hardErrors, softWarnings };
};

